import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_URL from "../../config";

const AIUsageDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [usageData, setUsageData] = useState([]);
  const [userData, setUserData] = useState({});
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalToken, setTotalToken] = useState({});

  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const debounceRef = useRef(null);

  const fetchAIUsageDetails = async (page = 1, date = selectedDate) => {
    try {
      setLoading(true);

      const params = {
        page,
        limit: pagination.limit,
      };

      if (date) {
        params.date = date;
      }

      const res = await axios.get(`${API_URL}/api/admin/ai-usage/${userId}`, {
        params,
      });

      const data = res.data?.data;

      console.log("AI Usage Response:", data);

      // NEW API RESPONSE
      setUsageData(data?.usage || []);
      setUserData(data?.user || {});
      setTotalToken(data.totalTokenUsage);

      setPagination(
        data?.pagination || {
          currentPage: 1,
          limit: 10,
          totalRecords: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      );
    } catch (error) {
      console.error("Error fetching AI usage details:", error);

      setUsageData([]);
      setUserData({});

      setPagination({
        currentPage: 1,
        limit: 10,
        totalRecords: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;

    fetchAIUsageDetails(1, "");

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [userId]);

  const handleDateChange = (e) => {
    const date = e.target.value;

    setSelectedDate(date);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchAIUsageDetails(1, date);
    }, 400);
  };

  const clearDate = () => {
    setSelectedDate("");

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchAIUsageDetails(1, "");
    }, 300);
  };

  const handlePageChange = (page) => {
    if (
      page < 1 ||
      page > pagination.totalPages ||
      page === pagination.currentPage
    ) {
      return;
    }

    fetchAIUsageDetails(page, selectedDate);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat("en-IN").format(number || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const [year, month, day] = date.split("-");

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    ).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate totals from currently returned usage records
  const currentPageTotals = usageData.reduce(
    (acc, item) => {
      acc.promptTokens += Number(item.promptTokens || 0);
      acc.completionTokens += Number(item.completionTokens || 0);
      acc.totalTokens += Number(item.totalTokens || 0);
      // acc.requests += Number(item.requests || 0);

      return acc;
    },
    {
      promptTokens: 0,
      completionTokens: 0,
      totalTokens: 0,
      // requests: 0,
    },
  );

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];

    for (let i = 1; i <= pagination.totalPages; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
        <small className="text-muted">
          Showing page {pagination.currentPage} of {pagination.totalPages}
        </small>

        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-light border"
            disabled={!pagination.hasPreviousPage || loading}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          >
            Previous
          </button>

          <div className="d-flex gap-1 flex-wrap">
            {pages.map((page) => (
              <button
                key={page}
                className={`btn ${
                  page === pagination.currentPage
                    ? "btn-primary"
                    : "btn-light border"
                }`}
                onClick={() => handlePageChange(page)}
                disabled={loading}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            className="btn btn-light border"
            disabled={!pagination.hasNextPage || loading}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="body-wrapper py-0">
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            ← Back
          </button>

          <div>
            <h4 className="fw-bold mb-1" style={{ color: "#6600cc" }}>
              AI Usage Details
            </h4>

            <small className="text-main">AI token usage history</small>
          </div>
        </div>

        <button
          className="btn btn-primary"
          onClick={() =>
            fetchAIUsageDetails(pagination.currentPage, selectedDate)
          }
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* SUMMARY CARDS */}
      <div className="row mb-2 mx-1">
        {/* USER */}
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "rgba(102, 0, 204, 0.15)",
                    color: "#6600cc",
                    width: "48px",
                    height: "48px",
                    fontSize: "22px",
                    fontWeight: 700,
                  }}
                >
                  {userData?.firstname?.charAt(0)?.toUpperCase() || "U"}
                </div>

                <div>
                  <h5 className="text-main mb-1">
                    {userData?.firstname || ""} {userData?.lastname || ""}
                  </h5>

                  <small className="text-heading">
                    {userData?.email || "-"}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PROMPT TOKENS */}
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <small className="text-heading">Prompt Tokens</small>

              <h4 className="fw-bold mt-2 mb-0 text-main">
                {formatNumber(totalToken.promptTokens)}
              </h4>

              <small className="text-muted">Current page</small>
            </div>
          </div>
        </div>

        {/* COMPLETION TOKENS */}
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <small className="text-heading">Completion Tokens</small>

              <h4 className="fw-bold mt-2 mb-0 text-main">
                {formatNumber(totalToken.completionTokens)}
              </h4>

              <small className="text-muted">Current page</small>
            </div>
          </div>
        </div>

        {/* TOTAL TOKENS */}
        <div className="col-xl-3 col-md-6 mb-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <small className="text-heading">Total Tokens</small>

              <h4 className="fw-bold mt-2 mb-0 text-main">
                {formatNumber(totalToken.totalTokens)}
              </h4>

              <small className="text-muted">Current page</small>
            </div>
          </div>
        </div>
      </div>

      {/* FILTER */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-5">
              <label className="form-label fw-semibold text-heading">
                Select Date
              </label>

              <input
                type="date"
                className="form-control"
                value={selectedDate}
                max={new Date().toISOString().split("T")[0]}
                onChange={handleDateChange}
              />
            </div>

            <div className="col-md-auto mt-3 mt-md-0">
              <button
                className="btn btn-light border"
                onClick={clearDate}
                disabled={!selectedDate || loading}
              >
                Clear Date
              </button>
            </div>

            {selectedDate && (
              <div className="col-md mt-3 mt-md-0">
                <div className="selected-date-box">
                  <small className="text-muted">Showing usage for</small>

                  <div className="fw-semibold">{formatDate(selectedDate)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <div
              className="spinner-border"
              role="status"
              style={{ color: "#6600cc" }}
            />

            <div className="mt-2 text-main">Loading AI usage...</div>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm">
          <div className="card-body">
            {/* TABLE HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="fw-semibold mb-1 text-heading">
                  {selectedDate ? "Selected Day Usage" : "AI Usage History"}
                </h5>

                <small className="text-main">
                  {selectedDate
                    ? `Token usage for ${formatDate(selectedDate)}`
                    : "Detailed AI token usage records"}
                </small>
              </div>

              <span
                className="badge"
                style={{
                  backgroundColor: "#f1e6ff",
                  color: "#6600cc",
                }}
              >
                {pagination.totalRecords}{" "}
                {pagination.totalRecords === 1 ? "Record" : "Records"}
              </span>
            </div>

            {usageData.length === 0 ? (
              <div className="text-center py-5">
                <div
                  className="mb-2"
                  style={{
                    fontSize: "35px",
                    color: "#6600cc",
                  }}
                >
                  ◷
                </div>

                <p className="text-main mb-0">
                  {selectedDate
                    ? "No AI usage found for this date."
                    : "No AI usage data found."}
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Used On</th>
                        <th>Provider</th>
                        <th>Prompt Tokens</th>
                        <th>Completion Tokens</th>
                        <th>Total Tokens</th>
                        {/* <th>Requests</th> */}
                        <th>Last Updated</th>
                      </tr>
                    </thead>

                    <tbody>
                      {usageData.map((item, index) => (
                        <tr key={`${item.date}-${item.lastUpdated}-${index}`}>
                          {/* DATE */}
                          <td>
                            <div className="fw-semibold text-black">
                              {formatDate(item.date)}
                            </div>
                          </td>

                          {/* USED ON */}
                          <td>
                            <span className="usage-purpose" title={item.usedOn}>
                              {item.usedOn || "N/A"}
                            </span>
                          </td>

                          {/* PROVIDER */}
                          <td>
                            <span className="provider-badge">
                              {item.provider || "-"}
                            </span>
                          </td>

                          {/* PROMPT */}
                          <td>{formatNumber(item.promptTokens)}</td>

                          {/* COMPLETION */}
                          <td>{formatNumber(item.completionTokens)}</td>

                          {/* TOTAL */}
                          <td>
                            <div className="total-token">
                              {formatNumber(item.totalTokens)}
                            </div>
                          </td>

                          {/* REQUESTS */}
                          {/* <td>{formatNumber(item.requests)}</td> */}

                          {/* LAST UPDATED */}
                          <td>
                            <small className="text-heading">
                              {formatDateTime(item.lastUpdated)}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {renderPagination()}
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .text-main {
          color: #6600cc;
        }

        .text-heading {
          color: #000;
        }

        .table th {
          white-space: nowrap;
          font-weight: 600;
        }

        .table td {
          white-space: nowrap;
        }

        .total-token {
          color: #6600cc;
          font-weight: 700;
          font-size: 15px;
        }

        .usage-purpose {
          display: inline-block;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
        }

        .provider-badge {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 20px;
          background: #f1e6ff;
          color: #6600cc;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .selected-date-box {
          background: #f8f5ff;
          border: 1px solid #eadcff;
          border-radius: 8px;
          padding: 9px 14px;
          display: inline-block;
          min-width: 180px;
        }

        .form-control:focus {
          border-color: #6600cc;
          box-shadow: 0 0 0 0.2rem rgba(102, 0, 204, 0.1);
        }

        .table tbody tr:hover {
          background-color: #faf8ff;
        }

        .btn-primary {
          background-color: #6600cc;
          border-color: #6600cc;
        }

        .btn-primary:hover,
        .btn-primary:focus {
          background-color: #5500aa;
          border-color: #5500aa;
        }
      `}</style>
    </div>
  );
};

export default AIUsageDetails;
